import { api } from "../api";
import { iconoLapiz, iconoBuscar, iconoMas } from "../iconos";
import type { Producto, Categoria, AreaProduccion } from "../../lib/types";

type ProductoFila = Producto & { categoria: string; area: string };

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

const PAGINA = 15;

export async function renderProductos(contenedor: HTMLElement): Promise<void> {
  let categorias: Categoria[] = [];
  let areas: AreaProduccion[] = [];
  let todos: ProductoFila[] = [];
  let filtro = "";
  let pagina = 1;

  try {
    const [catList, meta] = await Promise.all([
      api.get<Categoria[]>("/api/categorias"),
      api.get<{ areas: AreaProduccion[] }>("/api/meta"),
    ]);
    categorias = catList;
    areas = meta.areas;
  } catch {
    /* ignore */
  }

  // Siguiente codigo libre para la familia (prefijo) de una categoria.
  function siguienteCodigo(catId: number): string {
    const pref = categorias.find((c) => c.id === catId)?.prefijo;
    if (!pref) return "";
    const desde = pref * 1000;
    const hasta = desde + 999;
    const enBanda = todos.map((p) => p.codigo).filter((c) => c >= desde && c <= hasta);
    return String(enBanda.length ? Math.max(...enBanda) + 1 : desde + 1);
  }

  function avisoModal(mensaje: string): void {
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-2 text-lg font-bold text-red-700">Código duplicado</h2>
        <p class="text-sm text-masa-700">${mensaje}</p>
        <div class="mt-4 flex justify-end">
          <button id="ok" class="btn-primario">Entendido</button>
        </div>
      </div>`;
    dlg.querySelector("#ok")?.addEventListener("click", () => dlg.close());
    dlg.showModal();
  }
  const opc = (arr: Array<{ id: number; nombre: string }>, sel?: number) =>
    arr.map((x) => `<option value="${x.id}"${x.id === sel ? " selected" : ""}>${esc(x.nombre)}</option>`).join("");

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Productos</h1>
      <p class="subtitulo">Gestiona el catálogo de panadería y reposteria.</p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo producto
      </summary>
      <form id="f" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-6">
        <label class="sm:col-span-2"><span class="etiqueta">Código</span>
          <input id="codigo" type="number" class="campo" placeholder="1001" /></label>
        <label class="sm:col-span-4"><span class="etiqueta">Descripción</span>
          <input id="descripcion" class="campo" placeholder="Ej: Bizcochos Kg" /></label>
        <label class="sm:col-span-2"><span class="etiqueta">Categoría</span>
          <select id="categoria_id" class="campo">${opc(categorias)}</select></label>
        <label class="sm:col-span-2"><span class="etiqueta">Área</span>
          <select id="area_id" class="campo">${opc(areas)}</select></label>
        <label><span class="etiqueta">Empaque</span>
          <input id="empaque" type="number" min="1" value="1" class="campo" /></label>
        <label><span class="etiqueta">IVA</span>
          <select id="iva_tasa" class="campo">
            <option value="0">0%</option><option value="10">10%</option><option value="22">22%</option>
          </select></label>
        <div class="flex items-end sm:col-span-6">
          <button class="btn-primario">${iconoMas} Agregar</button>
          <span id="err" class="ml-3 self-center text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="relative mb-3 max-w-sm">
      <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-masa-400">${iconoBuscar}</span>
      <input id="buscar" class="campo pl-10" placeholder="Buscar por código o descripción..." />
    </div>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Código</th><th>Descripción</th><th>Categoría</th><th>Área</th>
          <th class="text-right">Empaque</th><th class="text-right">IVA</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
      <div id="pie" class="flex items-center justify-between border-t border-crema-200 px-3 py-2 text-sm text-masa-500"></div>
    </div>

    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const pie = contenedor.querySelector("#pie") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const buscar = contenedor.querySelector("#buscar") as HTMLInputElement;

  function filtrados(): ProductoFila[] {
    if (!filtro) return todos;
    const q = filtro.toLowerCase();
    return todos.filter(
      (p) => p.descripcion.toLowerCase().includes(q) || String(p.codigo).includes(q),
    );
  }

  function pintar(): void {
    const lista = filtrados();
    const totalPag = Math.max(1, Math.ceil(lista.length / PAGINA));
    if (pagina > totalPag) pagina = totalPag;
    const desde = (pagina - 1) * PAGINA;
    const pageItems = lista.slice(desde, desde + PAGINA);

    tbody.innerHTML =
      pageItems
        .map(
          (p) => `<tr>
            <td class="font-medium">${esc(p.codigo)}</td>
            <td>${esc(p.descripcion)}</td>
            <td>${esc(p.categoria)}</td>
            <td>${esc(p.area)}</td>
            <td class="text-right">x${esc(p.empaque)}</td>
            <td class="text-right">${esc(p.iva_tasa)}%</td>
            <td class="text-right"><button class="chip-accion" data-editar="${p.id}" title="Editar">${iconoLapiz}</button></td>
          </tr>`,
        )
        .join("") ||
      `<tr><td colspan="7" class="py-6 text-center text-masa-400">Sin resultados.</td></tr>`;

    const hasta = Math.min(desde + PAGINA, lista.length);
    pie.innerHTML = `
      <span>Mostrando ${lista.length ? desde + 1 : 0}–${hasta} de ${lista.length} productos</span>
      <span class="flex gap-1">
        <button id="prev" class="chip-accion" ${pagina <= 1 ? "disabled" : ""}>‹</button>
        <button id="next" class="chip-accion" ${pagina >= totalPag ? "disabled" : ""}>›</button>
      </span>`;
    pie.querySelector("#prev")?.addEventListener("click", () => { pagina--; pintar(); });
    pie.querySelector("#next")?.addEventListener("click", () => { pagina++; pintar(); });
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="7" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    todos = await api.get<ProductoFila[]>("/api/productos?activo=1");
    pintar();
  }

  buscar.addEventListener("input", () => { filtro = buscar.value.trim(); pagina = 1; pintar(); });

  // Alta: sugerir codigo segun la categoria (prefijo)
  const selCat = contenedor.querySelector("#categoria_id") as HTMLSelectElement;
  const inpCod = contenedor.querySelector("#codigo") as HTMLInputElement;
  const sugerir = () => { inpCod.value = siguienteCodigo(Number(selCat.value)); };
  selCat.addEventListener("change", sugerir);

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    const num = (id: string) => Number((contenedor.querySelector("#" + id) as HTMLInputElement).value);
    const txt = (id: string) => (contenedor.querySelector("#" + id) as HTMLInputElement).value.trim();
    if (!num("codigo") || !txt("descripcion")) {
      err.textContent = "Código y descripción son obligatorios";
      return;
    }
    const dupProd = todos.find((p) => p.codigo === num("codigo"));
    if (dupProd) {
      avisoModal(`El código ${num("codigo")} ya existe en el sistema (producto: ${esc(dupProd.descripcion)}).`);
      return;
    }
    try {
      await api.post("/api/productos", {
        codigo: num("codigo"), descripcion: txt("descripcion"),
        categoria_id: num("categoria_id"), area_id: num("area_id"),
        empaque: num("empaque"), iva_tasa: num("iva_tasa"),
      });
      form.reset();
      await cargar();
      sugerir();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  // Editar (delegacion)
  tbody.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-editar]");
    if (!btn) return;
    const id = Number(btn.getAttribute("data-editar"));
    const p = todos.find((x) => x.id === id);
    if (p) abrirEdicion(p);
  });

  function abrirEdicion(p: ProductoFila): void {
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[28rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar producto</h2>
        <p class="mb-4 text-sm text-masa-500">Código #${esc(p.codigo)}</p>
        <div class="grid grid-cols-2 gap-3">
          <label class="col-span-2"><span class="etiqueta">Descripción</span>
            <input id="e_desc" class="campo" value="${esc(p.descripcion)}" /></label>
          <label><span class="etiqueta">Categoría</span>
            <select id="e_cat" class="campo">${opc(categorias, p.categoria_id)}</select></label>
          <label><span class="etiqueta">Área</span>
            <select id="e_area" class="campo">${opc(areas, p.area_id)}</select></label>
          <label><span class="etiqueta">Empaque</span>
            <input id="e_emp" type="number" min="1" class="campo" value="${esc(p.empaque)}" /></label>
          <label><span class="etiqueta">IVA</span>
            <select id="e_iva" class="campo">
              ${[0, 10, 22].map((t) => `<option value="${t}"${t === p.iva_tasa ? " selected" : ""}>${t}%</option>`).join("")}
            </select></label>
          <label class="col-span-2 flex items-center gap-2 pt-1">
            <input id="e_activo" type="checkbox" ${p.activo ? "checked" : ""} class="h-4 w-4 accent-masa-600" />
            <span class="text-sm">Activo</span></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const val = (id: string) => (dlg.querySelector("#" + id) as HTMLInputElement).value;
      try {
        await api.put(`/api/productos/${p.id}`, {
          descripcion: val("e_desc").trim(),
          categoria_id: Number(val("e_cat")),
          area_id: Number(val("e_area")),
          empaque: Number(val("e_emp")),
          iva_tasa: Number(val("e_iva")),
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked ? 1 : 0,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
  sugerir();
}
